import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heart } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface CreateSamenGroupCardProps {
  onCreated?: () => void;
}

/**
 * Shown on the samen page to a HoofdnaarHart user who isn't in a group yet.
 * Previously this spot just told them to contact someone; now they can start
 * one themselves and invite the people they know.
 */
export function CreateSamenGroupCard({ onCreated }: CreateSamenGroupCardProps) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('create_samen_group', {
        p_name: trimmed,
      });
      if (error) throw error;

      toast({
        title: 'Je Samen groep staat klaar',
        description: 'Nodig nu de mensen uit met wie je samen wilt mediteren.',
      });
      onCreated?.();
    } catch (err) {
      console.error('Failed to create samen group:', err);
      toast({
        title: 'Aanmaken mislukt',
        description:
          err instanceof Error ? err.message : 'Probeer het later opnieuw.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="max-w-md w-full">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center gap-2 mb-6">
          <Heart className="h-8 w-8 text-primary" />
          <h1 className="text-xl font-semibold">Start je eigen Samen groep</h1>
          <p className="text-sm text-muted-foreground">
            Je zit nog niet in een Samen groep. Maak er zelf een aan en nodig
            mensen uit om samen te mediteren.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="samenName">Naam van de groep</Label>
            <Input
              id="samenName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijvoorbeeld: Onze ochtendmeditatie"
              maxLength={60}
              autoFocus
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={!name.trim() || submitting}
          >
            {submitting ? 'Bezig...' : 'Groep aanmaken'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
